import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import type { ReactNode } from 'react';

import { loginRequest } from './msal';

/**
 * Sign-in gate.
 *
 * Redirect flow rather than popup: popups are blocked by default in managed
 * browsers and break entirely when third-party cookies are partitioned, both
 * of which are realities on a corporate fleet. Redirect costs a page load and
 * is what MSAL recommends for SPAs.
 *
 * This gate is a *usability* boundary — it stops the shell rendering before an
 * identity exists. It is not the authorisation boundary: the API rejects an
 * unauthenticated or unauthorised request whatever the SPA renders (NFR-06).
 */

function SigningIn({ message }: { readonly message: string }) {
  return (
    <div className="centre">
      <h1>Contract Lifecycle Management</h1>
      <p className="mut">{message}</p>
    </div>
  );
}

function AuthFailure({ error }: { readonly error: Error | null }) {
  const { instance } = useMsal();
  return (
    <div className="centre">
      <h1>Sign-in did not complete</h1>
      <p className="mut">
        {error?.message ?? 'Entra ID did not return an identity for this session.'}
      </p>
      <button
        className="btn pri"
        type="button"
        onClick={() => void instance.loginRedirect(loginRequest)}
      >
        Try again
      </button>
    </div>
  );
}

export function AuthGate({ children }: { readonly children: ReactNode }) {
  const { inProgress } = useMsal();

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      loadingComponent={() => (
        <SigningIn
          message={
            inProgress === InteractionStatus.HandleRedirect
              ? 'Completing sign-in…'
              : 'Signing in with your cloudEQ account…'
          }
        />
      )}
      errorComponent={({ error }) => <AuthFailure error={error ?? null} />}
    >
      {children}
    </MsalAuthenticationTemplate>
  );
}
